import Link from "next/link";
import { jobPath } from "@/lib/job-url";
import type { JobListItem, JobLocation } from "@/lib/queries";
import {
  DEADLINE_TEXT_CLASS,
  deadlineInfo,
  isNew,
  locationsSummary,
  relativeTime,
} from "@/components/format";
import { OrgLogo } from "@/components/OrgLogo";
import { SaveJobButton } from "@/components/SaveJobButton";
import type { SaveJobInput } from "@/lib/saved-jobs";

/** Accent/case-insensitive key so "México" and "Mexico" collapse to one. */
function foldKey(s: string): string {
  return s
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim()
    .toLowerCase();
}

/** Distinct country names across a group's locations (first spelling wins). */
function distinctCountries(
  locations: JobLocation[] | null | undefined,
): string[] {
  if (!locations?.length) return [];
  const seen = new Map<string, string>();
  for (const loc of locations) {
    const name = loc?.country?.trim();
    if (!name) continue;
    const key = foldKey(name);
    if (!seen.has(key)) seen.set(key, name);
  }
  return [...seen.values()];
}

/** "A, B, C +2 more" — comma-joined with a tail count, or null if empty. */
function summarizeCommaList(labels: string[], max = 4): string | null {
  if (labels.length === 0) return null;
  const head = labels.slice(0, max).join(", ");
  const rest = labels.length - max;
  return rest > 0 ? `${head} +${rest} more` : head;
}

export function JobRow({
  job,
  now,
  isAuthed = false,
  isSaved = false,
}: {
  job: JobListItem;
  now: Date;
  isAuthed?: boolean;
  isSaved?: boolean;
}) {
  const deadline = deadlineInfo(job.deadline, now);
  const fresh = isNew(job.firstSeenAt, now);
  // Publish date (or first-seen for incrementally-discovered jobs) — never a
  // mass-import date, which reads as "posted late". See postedDisplay in queries.
  const published = job.postedDisplay;

  // A collapsed multi-location card: the same posting across several duty
  // stations. Summarize by distinct country (clean, readable — branch/city
  // labels often embed commas that blur together), with a chip that drills
  // into the individual listings. Falls back to city labels if no country.
  const grouped = (job.memberCount ?? 1) > 1;
  const location = grouped
    ? summarizeCommaList(distinctCountries(job.groupLocations)) ??
      locationsSummary(job.groupLocations ?? null, 3)
    : locationsSummary(job.locations);

  const metaParts = [
    job.orgName,
    location,
    job.contractType,
    job.remote ? "Remote" : null,
  ].filter(Boolean) as string[];

  return (
    <li
      className={`relative flex gap-3 px-3 py-3.5 transition-colors sm:gap-4 sm:px-4 ${
        job.featured
          ? "bg-brand-50/50 ring-1 ring-inset ring-brand-100 hover:bg-brand-50"
          : "hover:bg-neutral-50"
      }`}
    >
      <OrgLogo
        orgKey={job.orgKey}
        acronym={job.orgAcronym}
        name={job.orgName}
        className="mt-0.5 h-10 w-10"
      />

      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 leading-snug">
          <Link
            href={jobPath(job)}
            className="font-medium text-neutral-900 after:absolute after:inset-0 hover:text-brand-700"
          >
            {job.title}
          </Link>
          {job.featured ? (
            <span
              className="inline-flex items-center gap-1 rounded bg-brand-600 px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-white"
              title="Featured — a highlighted listing posted directly by the employer"
            >
              ★ Featured
            </span>
          ) : null}
          {fresh ? (
            <span
              className="inline-flex items-center rounded border border-brand-300 bg-white px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-brand-700"
              title="Added to the index in the last 24 hours"
            >
              new
            </span>
          ) : null}
          {job.grade ? (
            <span className="rounded border border-neutral-200 bg-neutral-50 px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-neutral-600">
              {job.grade}
            </span>
          ) : null}
          {grouped ? (
            <Link
              href={`/jobs?group=${encodeURIComponent(job.groupKey ?? "")}`}
              scroll={false}
              rel="nofollow"
              title="Same posting across multiple duty stations — view each listing"
              className="relative z-10 inline-flex items-center gap-1 rounded border border-brand-300 bg-white px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-brand-700 hover:border-brand-400 hover:bg-brand-50"
            >
              {job.memberCount} locations
            </Link>
          ) : null}
        </p>
        <p className="mt-1 truncate text-sm text-neutral-500">
          {metaParts.join(" · ")}
        </p>
        {deadline ? (
          <p
            className={`mt-1 text-xs font-medium sm:hidden ${DEADLINE_TEXT_CLASS[deadline.tone]}`}
          >
            {deadline.short}
          </p>
        ) : null}
      </div>

      <div className="flex shrink-0 flex-col items-end gap-1.5 pt-0.5 text-right">
        <SaveJobButton
          job={
            {
              jobId: job.id,
              title: job.title,
              orgKey: job.orgKey,
              orgLabel: job.orgAcronym ?? job.orgName,
              applyUrl: jobPath(job),
              location,
              closingAt: job.deadline ? job.deadline.toISOString() : null,
            } satisfies SaveJobInput
          }
          initialSaved={isSaved}
          isAuthed={isAuthed}
        />
        <div className="hidden flex-col items-end gap-1 sm:flex">
          {deadline ? (
            <span
              className={`text-xs font-medium ${DEADLINE_TEXT_CLASS[deadline.tone]}`}
            >
              {deadline.short}
            </span>
          ) : null}
          {published ? (
            <span className="text-xs text-neutral-400">
              posted {relativeTime(published, now)}
            </span>
          ) : null}
        </div>
      </div>
    </li>
  );
}
